import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { useCreatorProfile } from '../upload/useCreatorProfile'
import Dropzone from '../components/Dropzone/Dropzone'
import ProfileNameModal from '../components/Profile/ProfileNameModal'
import ToyDetailsModal, { type ToyDetails } from '../components/MusicPlayer/ToyDetailsModal'
import ToyReadyModal from '../components/MusicPlayer/ToyReadyModal'
import PackagingStatus from '../components/PackagingStatus'
import { type UploadFile } from '../upload/uploadAudio'
import { getCurrentIdentityId } from '../auth/currentIdentity'
import { useCurrentIo } from '../auth/useImpersonation'
import { packageAndUploadToy } from '../upload/toyPackaging'
import { isUploadWorking, type UploadState } from '../upload/uploadState'
import './MakeMusicPlayer.css'

export default function AdminToolMakeApp() {
  const { state: authState } = useAuth()
  const io = useCurrentIo()
  const {
    profile,
    creatorName,
    profileLoaded,
    profileSaving,
    profileError,
    needsProfileName,
    saveCreatorName,
  } = useCreatorProfile()

  const [project, setProject] = useState('')
  const [gitHash, setGitHash] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [upload, setUpload] = useState<UploadState>({ status: 'idle' })

  const intakeFiles = (incoming: File[]) => {
    if (incoming.length === 0) return
    setFiles((prev) => [...prev, ...incoming])
  }

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const trimmedProject = project.trim()
  const trimmedGitHash = gitHash.trim()

  const isWorking = isUploadWorking(upload)

  const canSubmit =
    authState.status === 'authenticated' &&
    profileLoaded &&
    !!creatorName &&
    trimmedProject.length > 0 &&
    trimmedGitHash.length > 0 &&
    !isWorking

  const handleDone = () => {
    if (!canSubmit) return
    setUpload({ status: 'collecting' })
  }

  const handleDetailsCancel = () => {
    if (upload.status === 'collecting') setUpload({ status: 'idle' })
  }

  const handleDetailsSubmit = async (details: ToyDetails) => {
    if (authState.status !== 'authenticated') return
    if (!io) return

    const creatorId = getCurrentIdentityId()

    const payloadFiles: UploadFile[] = files.map((f) => ({
      name: f.name,
      blob: f,
    }))

    try {
      await packageAndUploadToy({
        io,
        creatorId,
        creatorName,
        details,
        payloadFiles,
        firmwareTarget: { project: trimmedProject, gitHash: trimmedGitHash },
        appNameFallback: trimmedProject,
        onState: setUpload,
      })
    } catch (err) {
      console.error('[AdminToolMakeApp] packaging failed', err)
      const message = err instanceof Error ? err.message : 'Upload failed'
      setUpload({ status: 'error', message })
    }
  }

  return (
    <section className="music-player">
      <Link to="/admin-tools" className="detail-back">&larr; Back to Admin Tools</Link>
      <h1 className="page-title">Make App</h1>
      <p className="page-lede">
        Package an arbitrary firmware build into a toy. Type the firmware project + gitHash,
        optionally drop files to bundle, and we&rsquo;ll generate the manifest and upload everything
        to your toy catalog.
      </p>

      <div className="card music-player__panel">
        <label className="toy-modal__field" style={{ display: 'block', marginBottom: 12 }}>
          <span className="toy-modal__label">Project name</span>
          <input
            type="text"
            className="toy-modal__input"
            value={project}
            onChange={(e) => setProject(e.target.value)}
            placeholder="e.g. micropy-mp3-player"
            disabled={isWorking}
          />
        </label>
        <label className="toy-modal__field" style={{ display: 'block' }}>
          <span className="toy-modal__label">Git hash</span>
          <input
            type="text"
            className="toy-modal__input"
            value={gitHash}
            onChange={(e) => setGitHash(e.target.value)}
            placeholder="e.g. 354510f77756b41e770dbf6d8b00cb6a3dba586c"
            disabled={isWorking}
            spellCheck={false}
          />
        </label>
      </div>

      <Dropzone onFiles={intakeFiles}>
        <p className="music-player__dropzone-text">
          Drop any files to bundle into the app, or click to choose.<br />
          Files are uploaded as-is — no encoding or processing. Zero files is fine.
        </p>
      </Dropzone>

      {files.length > 0 && (
        <div className="card music-player__panel">
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {files.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 0',
                  borderBottom: i < files.length - 1 ? '1px solid var(--border, #eee)' : 'none',
                  fontFamily: 'var(--mono)',
                  fontSize: 14,
                }}
              >
                <span>
                  {f.name}{' '}
                  <span style={{ color: 'var(--ink-2)' }}>
                    ({Math.max(1, Math.round(f.size / 1024))} KB)
                  </span>
                </span>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => removeFile(i)}
                  disabled={isWorking}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="music-player__actions">
        <span className="music-player__count">
          {files.length} file{files.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canSubmit}
          onClick={handleDone}
        >
          {isWorking ? 'Working…' : 'Done, make my app'}
        </button>
      </div>

      <PackagingStatus state={upload} />

      <ToyDetailsModal
        open={upload.status === 'collecting'}
        onSubmit={handleDetailsSubmit}
        onCancel={handleDetailsCancel}
      />

      <ToyReadyModal open={upload.status === 'done'} />

      <ProfileNameModal
        open={needsProfileName}
        initialName={profile?.creatorName ?? ''}
        saving={profileSaving}
        error={profileError}
        onSubmit={saveCreatorName}
      />
    </section>
  )
}
